// LIBRERIE/IMPORTS
import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import he from 'he';

// HOOKS
import useArticoli, { ArticoloBlog } from '../../hooks/useGetArticoli';
import { useGetAgencyDetails } from "../../hooks/useGetAgencyDetails";

// CONFIGURAZIONI
import { appConfig } from "../../config/appConfig";

// COMPONENTI
import WidgetPartners from "../Widgets/WidgetPartners/WidgetPartners";
import WidgetContacts from "../Widgets/WidgetContacts/WidgetContacts";
import LoadingIndicator from "../Loaders/LoadingIndicator";
import NotFound from "../Error/NotFound";
import Pagination from '../Properties/Pagination';
import SubHeaderDefault from '../Header/subHeader/SubHeaderDefault';
import { WidgetMail } from '../Widgets/WidgetMail/WidgetMail';

const formatDate = ( dateString: string | null, t: (key: string) => string ): string => {
    if (!dateString) return t('blog.date.unavailable');
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            return t('blog.date.invalid');
        }
        return date.toLocaleDateString('it-IT', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
    } catch (e) {
        console.error("Error formatting date:", dateString, e);
        return t('blog.date.invalid');
    }
};

const LoadingSpinner: React.FC = () => {
    const { t } = useTranslation();
    return (
        <div className="text-center my-5">
            <div className="spinner-border text-primary" role="status">
                <span className="sr-only">{t('blog.loading')}...</span>
            </div>
            <p>{t('blog.loadingArticles')}</p>
        </div>
    );
};

const ErrorMessage: React.FC<{ message: string }> = ({ message }) => {
    const { t } = useTranslation();
    return (
        <div className="alert alert-danger" role="alert">
            {t('blog.errorMessage')} {message}
        </div>
    );
};

interface BlogProps {
    agencyId: number;
}

const Blog: React.FC<BlogProps> = ({ agencyId }) => {
    const { t } = useTranslation();
    const { agency, loading: agencyLoading, error: agencyError } = useGetAgencyDetails(agencyId);
    const { articles, isLoading, error, pagination, fetchArticoli } = useArticoli({
        agencyId: agencyId,
        initialLimit: 6,
    });


    const staticPartnerUrl = agencyId
        ? `http://www.casain24ore.it/fromsito.php?agReg=${agencyId}`
        : 'http://www.casain24ore.it'; // Fallback se agencyId non è valido
    const staticPartner = {
        url: staticPartnerUrl,
        data: { imgSrc: '/assets/images/c24.png', imgAlt: 'Network Immobiliare' }
    };
    const rawDynamicPartnersData = appConfig.content.partners;
    const validDynamicPartnersData = rawDynamicPartnersData.filter(partner =>
        partner && partner.imgSrc && partner.imgSrc.trim() !== ''
    );
    const formattedDynamicPartners = validDynamicPartnersData.map(partner => ({
        url: partner.url || '#',
        data: {
            imgSrc: partner.imgSrc as string,
            imgAlt: partner.imgAlt || 'Partner Logo'
        }
    }));
    const allPartnersForWidget = [staticPartner, ...formattedDynamicPartners];

    const handlePageChange = (page: number) => { fetchArticoli(page); };

    if (agencyLoading) return <LoadingIndicator />;
    if (agencyError) return <NotFound message={t('blog.loadingError')} />;

    const renderArticle = (article: ArticoloBlog) => {
        const detailUrl = `/articolo/${article.id}-${article.seoString}`;
        const imageUrl = article.foto ? `https://www.casagest24.it/${article.foto}` : '/images/blog/placeholder.jpg';

        return (
            <div className="col-12 col-lg-6">
                <Link to={detailUrl} className="p-3 mb-3 blog-card" key={article.id}>
                    <img className="img-fluid" src={imageUrl} alt={`${article.titolo || t('blog.article.imageAltFallback')}`} />
                    <div className="py-3">
                        {article.nomeCategoria && (
                            <div className="blog-post-category mb-2">
                                <strong>{article.nomeCategoria}</strong>
                            </div>
                        )}
                        <h5 className="mb-3">{he.decode(article.titolo)}</h5>
                        <p className="mb-2">{he.decode(`${article.testoRidotto}`) || t('blog.article.noShortDescription')}</p>
                        <div className="public-data">
                            <i className="far fa-clock mr-1"></i>
                            <span>{formatDate(article.dataPubblicazione, t)}</span>
                        </div>
                    </div>
                </Link>
            </div>
        );
    }

    return (
        <>
            <Helmet>
                <title>{t('blogPage.metaTitle')}</title>
                <meta name="description" content={t('blogPage.metaDescription') as string} />
            </Helmet>

            <SubHeaderDefault title={t('blogPage.pageTitle')} isSeo={true} />

            <section className="space-ptb">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-12 text-center">
                            <h2 className="mb-5">{t('blogPage.title')}</h2>
                            <p className="mb-4" dangerouslySetInnerHTML={{ __html: t('blogPage.paragraph') as string }}></p>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-lg-8">
                            <div className="row">
                                {isLoading && <LoadingSpinner />}
                                {error && <ErrorMessage message={error} />}
                                {!isLoading && !error && articles.length === 0 && (
                                    <p>{t('blogPage.notFoundMessage')}</p>
                                )}
                                {!isLoading && !error && articles.length > 0 && (
                                    <>
                                        {articles.map(renderArticle)}

                                        <Pagination
                                            count={pagination.totalItems} // Passa il numero totale di articoli
                                            limit={pagination.limit} // Passa il limite di articoli per pagina
                                            currentPage={pagination.currentPage} // Passa la pagina corrente
                                            onPageChange={handlePageChange} // Passa la funzione di callback
                                            siblingCount={1} // Quanti "siblings" ci sono [ Es. {1} ]--> { 1 ... 4 [5] 6 ... 43 }
                                        />
                                    </>
                                )}
                            </div>
                        </div>
                        <div className="col-lg-4">
                            <div className="mb-4">
                                <WidgetMail />
                            </div>
                            <div className="mb-4">
                                <WidgetContacts
                                    agencyDataAddress={agency?.address_details?.address ?? ''}
                                    agencyDataCity={agency?.address_details?.city_name ?? ''}
                                    agencyDataComuneCapoluogo={agency?.address_details?.city_name === agency?.address_details?.province_name}
                                    agencyDataProvinciaSigla={agency?.address_details?.sigla ?? ''}
                                    agencyDataEmail={appConfig.agency.agencyEmail}
                                    agencyDataEmailShow={appConfig.ui.agencyWidget.showEmail}
                                    agencyDataPhone1={agency?.phone1 ?? ''}
                                    agencyDataPhone2={agency?.phone2 ?? ''}
                                    agencyDataInternationalPrefix={appConfig.agency.intlPrefix ?? '+39'}
                                    agencyDataMobile={agency?.mobile ?? ''}
                                    agencyDataMobile2={agency?.mobile2 ?? ''}
                                    agencyDataFax={agency?.fax ?? ''}
                                    agencySocialShow={appConfig.ui.agencyWidget.showSocialLinks}
                                    agencyDataWhatsapp={appConfig.socialLinks.whatsapp}
                                    agencyDataFacebook={appConfig.socialLinks.facebook}
                                    agencyDataInstagram={appConfig.socialLinks.instagram}
                                    agencyDataTwitter={appConfig.socialLinks.twitter}
                                    agencyDataLinkedin={appConfig.socialLinks.linkedin}
                                    agencyDataPinterest={appConfig.socialLinks.pinterest}
                                    agencyDataYoutube={appConfig.socialLinks.youtube}
                                />
                            </div>
                            <div>
                                <WidgetPartners partners={allPartnersForWidget} />
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default Blog;
